import {
  IUomConversionAuditConversionPayload,
  IUomConversionAuditRow,
} from 'companySettings/types';

import { IBulkUpsertUomConversionsForm } from './types';

/**
 * The parts a conversion can actually be written for.
 *
 * The report has one row per part + extracted UOM, so a part quoted in two units
 * appears twice; deduplicated by part id so the count the user is shown matches
 * the conversions written, and so two rows cannot collide on the server's
 * (company, part, manufacturer, from_uom, to_uom) unique constraint.
 *
 * A row with no manufacturer is dropped: the conversion is keyed by
 * (part number, manufacturer), so there is nothing to write it against.
 */
export const getConversionPartIds = (rows: IUomConversionAuditRow[]): number[] => [
  ...new Set((rows ?? []).filter((row) => null !== row.manufacturerId).map((row) => row.partId)),
];

/**
 * One conversion per selected part, all carrying the pair and multiplier the
 * user entered.
 */
export const mapConversionsPayload = (
  rows: IUomConversionAuditRow[],
  data: IBulkUpsertUomConversionsForm
): IUomConversionAuditConversionPayload[] => {
  const partIds = new Set(getConversionPartIds(rows));
  const byPart = new Map<number, IUomConversionAuditConversionPayload>();

  (rows ?? []).forEach((row) => {
    if (!partIds.has(row.partId)) {
      return;
    }

    byPart.set(row.partId, {
      part: row.partNumber,
      manufacturerId: row.manufacturerId as number,
      fromUom: data.baseUom?.name as string,
      toUom: data.alternateUom as string,
      multiplier: Number(data.multiplier),
    });
  });

  return [...byPart.values()];
};

export interface ISelectionUomPair {
  /** The one Base UOM the selected parts share, or null when none or several. */
  baseUom: string | null;
  /** The one Extracted UOM the selected rows share, or null when none or several. */
  extractedUom: string | null;
  /** True when the selection spans more than one Base UOM or Extracted UOM. */
  isMixed: boolean;
}

const distinct = (values: (string | null | undefined)[]): string[] => [
  ...new Set(values.filter((value): value is string => !!value)),
];

/**
 * The UOM pair a selection can take as one conversion.
 *
 * The drawer writes the same (base, alternate) pair to every selected part, but
 * a row only clears when that pair is the row's own Base UOM and Extracted UOM.
 * So the pair is read off the rows: when they agree it seeds the form, and when
 * they disagree the action is refused rather than saving conversions that never
 * match. Rows with no value for a side (No Base UOM, No Extracted UOM) neither
 * seed nor block. The extracted UOM comes back lower-cased from the report and
 * is upper-cased here to match how UOMs are written everywhere else; matching is
 * case-insensitive either way.
 */
export const getSelectionUomPair = (rows: IUomConversionAuditRow[]): ISelectionUomPair => {
  const bases = distinct((rows ?? []).map((row) => row.baseUom));
  const extracted = distinct((rows ?? []).map((row) => row.extractedUom));

  return {
    baseUom: 1 === bases.length ? bases[0] : null,
    extractedUom: 1 === extracted.length ? extracted[0].toUpperCase() : null,
    isMixed: bases.length > 1 || extracted.length > 1,
  };
};
